"use client";

import { format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { AuthSurface } from "@/components/auth";
import { MfaDialog } from "@/components/mfa";
import { Button, Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui";
import { DatePicker } from "@/components/ui/date-picker";
import { waitlistTimePreferenceLabels } from "@/components/waitlist/waitlist-time-preference";
import { useSelfScheduleContext } from "@/contexts/self-schedule-provider";
import { usePatientClaim } from "@/hooks/patient-claim/use-patient-claim";
import { clinicClockFor, dayMarker, pickedDay } from "@/lib/clinic-clock";
import { resolveServiceOccurrences } from "@/lib/service-addon-utils";
import { firstValidTimezone, useStoreTimezone } from "@/lib/timezone";
import { TimeOfDay } from "@/types/self-schedule";
import { PatientWaitlistRequest, joinPatientWaitlist } from "@/api/check-in/self-schedule/self-schedule";
import { SelfScheduleStep } from "@/stores/self-schedule-store";
interface JoinWaitlistProps {
  selectedDate: Date;
}
export default function JoinWaitlist({
  selectedDate
}: JoinWaitlistProps) {
  const {
    selectedServices,
    selectedLocation,
    requestedAssignments,
    addonSelections,
    selectedAddons,
    availableProviders,
    setCurrentStep
  } = useSelfScheduleContext();
  const servicesById = new Map(resolveServiceOccurrences(selectedServices ?? [], selectedAddons ?? {}).flatMap(({
    service,
    addons
  }) => [service, ...addons.map(addon => addon.addonService)].map(item => [item.id, {
    name: item.name,
    allowIndividualProviderBooking: "allowIndividualProviderBooking" in item && item.allowIndividualProviderBooking === true
  }] as const)));
  const hasProviderPreferences = requestedAssignments.some(assignment => servicesById.get(assignment.serviceId)?.allowIndividualProviderBooking === true);
  const {
    claimedPatient
  } = usePatientClaim();
  const [open, setOpen] = useState(false);
  const [verificationId, setVerificationId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [timePreference, setTimePreference] = useState<TimeOfDay | "any">("any");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const submitting = useRef(false);
  const verificationSequence = useRef(0);
  const pendingRequest = useRef<{
    verificationId: number;
    data: PatientWaitlistRequest;
  } | null>(null);
  const hasSelection = !!selectedLocation?.id && requestedAssignments.length > 0;
  // Today on the location's clock; picked days compare as "YYYY-MM-DD".
  // A patient page has no user context, so the fallback is the store's zone.
  const storeTimeZone = useStoreTimezone();
  const todayDay = clinicClockFor(firstValidTimezone(selectedLocation?.timezone, storeTimeZone) ?? storeTimeZone).today();
  const today = dayMarker(todayDay);
  const invalidDates = !startDate || !endDate || pickedDay(startDate) < todayDay || pickedDay(endDate) < pickedDay(startDate);
  useEffect(() => {
    return () => {
      pendingRequest.current = null;
    };
  }, []);
  const closePreferences = () => {
    if (submitting.current) return;
    pendingRequest.current = null;
    setVerificationId(null);
    setOpen(false);
  };
  const submit = async (data: PatientWaitlistRequest) => {
    if (submitting.current) return;
    submitting.current = true;
    setPending(true);
    setError("");
    try {
      await joinPatientWaitlist(data);
      setOpen(false);
      setCurrentStep(SelfScheduleStep.WAITLIST_COMPLETE, "forward");
    } catch (failure) {
      const duplicate = (failure as {
        response?: {
          status?: number;
        };
      })?.response?.status === 409;
      setError(duplicate ? "You're already on the waitlist for these preferences. You can change your preferences and try again." : "Couldn't join the waitlist. Please try again.");
    } finally {
      submitting.current = false;
      setPending(false);
    }
  };
  const handleJoin = () => {
    if (!hasSelection || invalidDates || !startDate || !endDate || submitting.current || pendingRequest.current) {
      return;
    }
    const data_0: PatientWaitlistRequest = {
      assignments: requestedAssignments,
      addonSelections: Object.keys(addonSelections).length ? addonSelections : undefined,
      locationId: selectedLocation!.id,
      startDate: format(startDate, "yyyy-MM-dd"),
      endDate: format(endDate, "yyyy-MM-dd"),
      timePreference: timePreference === "any" ? null : timePreference
    };
    if (claimedPatient?.id) {
      void submit(data_0);
    } else {
      const nextVerificationId = ++verificationSequence.current;
      pendingRequest.current = {
        verificationId: nextVerificationId,
        data: data_0
      };
      setVerificationId(nextVerificationId);
    }
  };
  return <>
      <Button variant="primary" disabled={pending} onClick={() => {
      const date = pickedDay(selectedDate) < todayDay ? today : selectedDate;
      pendingRequest.current = null;
      setVerificationId(null);
      setStartDate(date);
      setEndDate(date);
      setTimePreference("any");
      setError("");
      setOpen(true);
    }}>
        Join waitlist
      </Button>
      <Dialog open={open && verificationId === null} onOpenChange={value => {
      if (!value) closePreferences();
    }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Join waitlist</DialogTitle>
            <DialogDescription>
              Choose your preferences. Joining the waitlist does not book an
              appointment.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="space-y-1">
              <Label>Selected appointment</Label>
              <ol className="space-y-1 text-body">
                {requestedAssignments.map((assignment_0, index) => {
                const service_0 = servicesById.get(assignment_0.serviceId);
                const showProvider = service_0?.allowIndividualProviderBooking === true;
                const provider = availableProviders?.find(item_0 => item_0.id === assignment_0.providerId);
                const providerName = provider ? [provider.firstName, provider.lastName].filter(Boolean).join(" ") : assignment_0.providerId;
                return <li key={`${assignment_0.serviceId}:${index}`}>
                      <span>{index + 1}. </span>
                      <span>{service_0?.name ?? assignment_0.serviceId}</span>
                      {showProvider && <>
                          <span> — </span>
                          <span>{providerName || "Any eligible provider"}</span>
                        </>}
                    </li>;
              })}
              </ol>
              <p className="text-caption text-foreground-secondary">
                <span>
                  <span>
                    {hasProviderPreferences ? "All selected services, quantities, add-ons and provider preferences are saved together." : "All selected services, quantities and add-ons are saved together."}
                  </span>{" "}
                  <span>
                    The clinic will check availability before booking.
                  </span>
                </span>
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="patient-waitlist-start">Start date</Label>
              <DatePicker id="patient-waitlist-start" aria-label="Start date" value={startDate} onChange={setStartDate} fromDate={today} disabled={pending} hasError={!!startDate && pickedDay(startDate) < todayDay} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="patient-waitlist-end">End date (inclusive)</Label>
              <DatePicker id="patient-waitlist-end" aria-label="End date (inclusive)" value={endDate} onChange={setEndDate} fromDate={startDate && pickedDay(startDate) >= todayDay ? startDate : today} disabled={pending} hasError={!!startDate && !!endDate && invalidDates} />
              {!!startDate && !!endDate && invalidDates && <p role="alert" className="text-caption text-status-error-text">
                  Choose dates from today onward, with the end date on or after
                  the start date.
                </p>}
              <p className="text-caption text-foreground-secondary">
                Both dates are included, even if no times are currently
                available.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="patient-waitlist-time">
                Time preference (optional)
              </Label>
              <Select value={timePreference} onValueChange={value_0 => setTimePreference(value_0 as TimeOfDay | "any")} disabled={pending}>
                <SelectTrigger id="patient-waitlist-time">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(waitlistTimePreferenceLabels).map(([value_1, label]) => <SelectItem key={value_1} value={value_1}>
                        {label}
                      </SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {error && <p role="alert" className="text-body text-status-error-text">
                {error}
              </p>}
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" disabled={pending} onClick={closePreferences}>
              Cancel
            </Button>
            <Button disabled={!hasSelection || invalidDates || pending} isLoading={pending} onClick={handleJoin}>
              {claimedPatient?.id ? "Join waitlist" : "Verify and join waitlist"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {verificationId !== null && <MfaDialog key={verificationId} open onOpenChange={value_2 => {
      if (value_2 || pendingRequest.current?.verificationId !== verificationId) {
        return;
      }
      setVerificationId(null);
      // MfaDialog closes immediately before its verified callback. Give
      // that synchronous callback a chance to consume the request; a
      // cancelled or later-resolving attempt must not enqueue it.
      queueMicrotask(() => {
        if (pendingRequest.current?.verificationId === verificationId) {
          pendingRequest.current = null;
        }
      });
    }} surface={AuthSurface.SELF_SCHEDULE} description="Verify your phone number to join the waitlist." tAndSFooterText="joining the waitlist" onPatientVerified={() => {
      const request = pendingRequest.current;
      if (request?.verificationId !== verificationId) return;
      pendingRequest.current = null;
      setVerificationId(null);
      void submit(request.data);
    }} />}
    </>;
}